// Ask the peer origin's `/_session-bridge` for its ACCESS token and adopt it
// as this origin's access-only session. Resolves `{ access_token, expires_at }`
// (expires_at in epoch ms, or null) or null.
//
// The bridge hands over the access token only (sessionBridgeTokens.js). The
// origin that owns the login keeps its refresh token and is the only one that
// refreshes; a peer origin re-asks here instead:
//   - at cold boot, when it has no fresh session (`ifStale: true`) — an origin
//     that holds its OWN refresh token never borrows here: it refreshes itself;
//   - when its access token runs out (the SDK session reacquirer —
//     TokenManager.setSessionReacquirer, wired in sdkClient.js — or the
//     renewal timer in sessionBridgeRenewal.js for an SDK without it);
//   - after the server REJECTED its token, once, before /login (bootResolve).
//     There `rejectedToken` is the token the server just refused: a reply with
//     that same token is no session, whatever its local expiry says.
//
// Adoption writes the access token + expiry and DROPS any refresh token this
// origin held — a session borrowed from a peer must never be refreshed here
// (a copied single-use refresh token is the sign-out this fixes). Stale
// `symbols_bridge_*` mirrors and `symbols_expires_in` go with it.
//
// Never sends anything but `{ type: 'symbols-session-request' }`, and only to
// the exact peer origin; replies from any other origin are ignored.
//
// FA204 / frank trap — helpers stay inline in the export; the peer resolver
// is a cross-file import.

import { sessionBridgePeerOrigin } from './sessionBridgePeerOrigin.js'

export const reacquireBridgeSession = ({
  ifStale = false,
  rejectedToken = null,
  timeoutMs = 4000,
  bridgePath = '/_session-bridge',
  // Called ONLY when the peer answers an explicit ok:false ("no session
  // here") — never on a timeout, an error or an unusable reply, which all
  // resolve null too. sdkClient restores a refresh token as this origin's own
  // login on this verdict alone (review D1, live on canvas.dev 2026-10-11).
  onNoSession = null
} = {}) =>
  new Promise((resolve) => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      resolve(null)
      return
    }
    let storage = null
    try {
      storage = window.localStorage
    } catch {
      storage = null
    }
    if (!storage) {
      resolve(null)
      return
    }
    if (ifStale) {
      if (storage.getItem('symbols_refresh_token')) {
        resolve(null)
        return
      }
      const access = storage.getItem('symbols_access_token')
      const exp = Number(storage.getItem('symbols_expires_at'))
      if (access && (!(exp > 0) || exp > Date.now())) {
        resolve(null)
        return
      }
    }
    const origin = sessionBridgePeerOrigin()
    if (!origin || origin === window.location.origin) {
      resolve(null)
      return
    }

    const iframe = document.createElement('iframe')
    iframe.style.cssText = 'position:absolute;width:0;height:0;border:0;visibility:hidden'
    iframe.src = `${origin}${bridgePath}`
    let settled = false
    let timer = null
    const ask = () => {
      try {
        iframe.contentWindow?.postMessage({ type: 'symbols-session-request' }, origin)
      } catch {
        /* frame gone — the timeout settles */
      }
    }
    const done = (value) => {
      if (settled) return
      settled = true
      if (timer) clearTimeout(timer)
      window.removeEventListener('message', onMessage)
      try {
        iframe.remove()
      } catch {
        /* already detached */
      }
      resolve(value)
    }
    const adopt = (tokens) => {
      const access = tokens?.symbols_access_token || tokens?.symbols_bridge_access_token
      const rawExp = Number(tokens?.symbols_expires_at || tokens?.symbols_bridge_expires_at)
      const expiresAt = rawExp > 0 ? rawExp : null
      if (!access || typeof access !== 'string') return null
      if (expiresAt && expiresAt <= Date.now()) return null
      if (rejectedToken && access === rejectedToken) return null
      try {
        storage.setItem('symbols_access_token', access)
        if (expiresAt) storage.setItem('symbols_expires_at', String(expiresAt))
        else storage.removeItem('symbols_expires_at')
        for (const k of [
          'symbols_refresh_token',
          'symbols_expires_in',
          'symbols_bridge_access_token',
          'symbols_bridge_expires_at'
        ]) {
          storage.removeItem(k)
        }
        // Marks this origin's session as ADOPTED from the bridge. Only such a
        // session gets the SDK reacquirer and the boot gate's one re-ask
        // (review M2): an origin that owns its login never borrows.
        storage.setItem('symbols_session_source', 'bridge')
      } catch {
        return null
      }
      return { access_token: access, expires_at: expiresAt }
    }
    // Only THIS request's bridge iframe may answer: the exact peer origin AND
    // the iframe's own window. Any other window of the peer origin (an
    // opener, a popup, user code on that origin) could otherwise post a
    // forged response mid-ask and make this origin adopt its token
    // (review M1, 2026-10-09).
    const onMessage = (ev) => {
      if (ev.origin !== origin || ev.source !== iframe.contentWindow) return
      const type = ev.data?.type
      if (type === 'symbols-session-ready') {
        ask()
        return
      }
      if (type !== 'symbols-session-response') return
      if (ev.data?.ok) {
        done(adopt(ev.data.sdkTokens))
        return
      }
      if (typeof onNoSession === 'function') {
        try {
          onNoSession()
        } catch {
          /* the caller's verdict hook must not keep the ask open */
        }
      }
      done(null)
    }
    window.addEventListener('message', onMessage)
    iframe.addEventListener('load', ask)
    timer = setTimeout(() => done(null), timeoutMs)
    try {
      document.body.appendChild(iframe)
    } catch {
      done(null)
    }
  })
